import { Link } from "react-router-dom";
import { ArrowRight, Zap, Star, Puzzle, Layers, HeartHandshake, DoorOpen } from "lucide-react";
import Reveal from "@/components/Reveal";

const domains = [
  {
    icon: Zap,
    id: "tdah",
    title: "TDAH",
    text: "Comprendre l'attention, l'impulsivité et l'hyperactivité — et apprendre à piloter ce moteur de Formule 1 au quotidien, à l'école comme à la maison.",
  },
  {
    icon: Star,
    id: "hp",
    title: "Haut potentiel (HP)",
    text: "Un fonctionnement rapide et intense, souvent mal compris. Donner au jeune un récit dans lequel se reconnaître, loin des étiquettes.",
  },
  {
    icon: Puzzle,
    id: "multi-dys",
    title: "Troubles DYS",
    text: "Dyslexie, dyspraxie, dyscalculie… Déconstruire les mythes et mettre en place des stratégies concrètes pour les apprentissages.",
  },
  {
    icon: Layers,
    id: "tsa",
    title: "TSA (autisme)",
    text: "Accueillir un fonctionnement différent, comprendre les besoins sensoriels et sociaux, et favoriser l'inclusion scolaire et familiale.",
  },
  {
    icon: HeartHandshake,
    id: "anxio-depression",
    title: "Anxio-dépression",
    text: "Accueillir les émotions qui débordent ou qui éteignent, et reconstruire pas à pas un sentiment de sécurité et de capacité.",
  },
  {
    icon: DoorOpen,
    id: "phobie-scolaire",
    title: "Phobie scolaire",
    text: "Quand l'école devient impossible. Un accompagnement progressif, sans culpabilisation, pour retrouver le chemin de la classe.",
  },
];

export default function Accompagnement() {
  return (
    <div data-testid="accompagnement-page">
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <Reveal>
          <p className="font-display text-xs tracking-[0.2em] uppercase text-taupe">Accompagnement</p>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight tracking-tight text-charcoal sm:text-5xl">
            Décoder ensemble un cerveau qui ne s'arrête jamais
          </h1>
          <p className="mt-8 max-w-prose text-base leading-relaxed text-stone-700">
            J'accompagne les jeunes de 10 à 20 ans en difficulté scolaire ou familiale — souvent à
            la suite d'un diagnostic. Avec eux et leurs parents, nous transformons ce qui semble
            chaotique en quelque chose de lisible, vivable, et même parfois étonnant.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {domains.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 0.08}>
              <article
                data-testid={`domain-card-${d.id}`}
                className="h-full rounded-2xl border border-stone-200 bg-sand p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-sm"
              >
                <d.icon size={28} className="text-taupe" strokeWidth={1.5} />
                <h2 className="mt-6 font-display text-xl text-charcoal">{d.title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-stone-700">{d.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
          <Reveal>
            <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">Ma posture</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl tracking-tight text-charcoal sm:text-3xl">
              Une culture de la neurodiversité
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-12 md:grid-cols-3">
            {[
              {
                title: "Expliquer sans stigmatiser",
                text: "Nommer ce qui se joue, avec des mots justes, sans jamais réduire un jeune à son diagnostic.",
              },
              {
                title: "Normaliser sans banaliser",
                text: "Oui, votre cerveau fonctionne autrement — et c'est une réalité qui mérite d'être prise au sérieux, pas minimisée.",
              },
              {
                title: "Outiller sans culpabiliser",
                text: "Des stratégies concrètes, testées ensemble, sans injonctions ni reproches. On avance à votre rythme.",
              },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div data-testid={`posture-${i}`} className="border-l-2 border-taupe pl-6">
                  <h3 className="font-display text-xl text-charcoal">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-700">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="accompagnement-contact-cta"
              className="mt-16 inline-flex items-center gap-2 rounded-full bg-taupe px-7 py-3 font-display text-sm text-bone transition-colors hover:bg-taupe-dark"
            >
              Discuter de votre situation <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
